import { useState, useCallback, useEffect } from 'react';
import { Wand2 } from 'lucide-react';
import { ItemInput } from './components/ItemInput';
import { ScoringView } from './components/ScoringView';
import { ResultsView } from './components/ResultsView';
import type { MagicPrioritizationItem, ScoringPhase } from './types';
import { calculateWeightedScore, getCd3Category } from './types';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { MagicPrioritizationLive } from '../../components/prioritization/session/liveExperience/liveTools/MagicPrioritizationLive';

type AppState = 'input' | 'scoring' | 'results';

/**
 * Magic Prioritization Trick
 * 
 * Based on John Cutler's work on Cost of Delay and Joshua Arnold's qualitative CoD.
 * A staged decoupling approach that separates urgency, value, and duration judgments
 * to prevent teams from collapsing them into a single intuitive "this feels important."
 * 
 * THE KEY INSIGHT: Score ALL items on one dimension before moving to the next dimension.
 * This prevents unconsciously conflating urgency, value, and duration when scoring.
 * 
 * Flow:
 * 1. Rate ALL items on Urgency (ASAP / Soon / Whenever)
 * 2. Rate ALL items on Value (Game Changer / Optimizer / Tweak)
 * 3. Rate ALL items on Duration (1-3 months / 1-3 quarters / 1-3 years)
 * 4. View results with calculated CD3 scores
 * 
 * References:
 * - https://cutlefish.substack.com/p/tbm-245-the-magic-prioritization
 * - https://blackswanfarming.com/qualitative-cost-delay/
 */
interface MagicPrioritizationContentProps {
  onRegisterImportHandler?: (handler: (items: ToolItem[]) => void) => void;
  onRegisterGetResults?: (handler: (() => ToolResultItem[]) | null) => void;
  onItemCountChange?: (count: number) => void;
}

function MagicPrioritizationContent({ onRegisterImportHandler, onRegisterGetResults, onItemCountChange }: MagicPrioritizationContentProps = {}) {
  const { isLiveSession } = useLiveToolSession();
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});
  const [state, setState] = useState<AppState>('input');
  const [items, setItems] = useState<MagicPrioritizationItem[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [scoringPhase, setScoringPhase] = useState<ScoringPhase>('urgency');

  const handleStart = (itemNames: string[]) => {
    const newItems: MagicPrioritizationItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      urgency: null,
      value: null,
      duration: null,
      weightedScore: null,
      cd3Category: null,
    }));
    setItems(newItems);
    setCurrentItemIndex(0);
    setScoringPhase('urgency');
    setState('scoring');
  };

  // Handler for importing items from workspace (app mode)
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: MagicPrioritizationItem[] = toolItems.map((item) => ({
      id: item.id,
      name: item.name,
      urgency: null,
      value: null,
      duration: null,
      weightedScore: null,
      cd3Category: null,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    
    setItems(prev => [...prev, ...newItems]);
    setCurrentItemIndex(0);
    setScoringPhase('urgency');
    setState('scoring');
  }, []);

  // Register import handler with outer wrapper so ToolWrapper can show the selector screen
  useEffect(() => {
    onRegisterImportHandler?.(handleImportItems);
  }, [handleImportItems, onRegisterImportHandler]);

  // Report item count to outer wrapper so zero state hides once items are loaded
  useEffect(() => {
    onItemCountChange?.(items.length);
  }, [items.length, onItemCountChange]);

  // Handler for getting results
  const getResults = useCallback((): ToolResultItem[] => {
    return items.map((item, index) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      rank: index + 1,
      score: item.weightedScore || 0,
      category: item.cd3Category || undefined,
      metadata: {
        urgency: item.urgency,
        value: item.value,
        duration: item.duration,
        cd3Category: item.cd3Category,
      },
    }));
  }, [items, sourceIdMap]);

  const allDimensionsScored =
    items.length > 0 && items.every(i => i.urgency && i.value && i.duration);
  const canSubmit = allDimensionsScored && items.some(i => i.weightedScore != null);

  // Register getResults handler with outer wrapper so ToolWrapper save button works
  useEffect(() => {
    onRegisterGetResults?.(canSubmit || state === 'results' ? getResults : null);
  }, [getResults, onRegisterGetResults, canSubmit, state]);

  const handleUrgencyUpdate = (value: MagicPrioritizationItem['urgency']) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      urgency: value,
    };
    setItems(updatedItems);
  };

  const handleValueUpdate = (value: MagicPrioritizationItem['value']) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      value: value,
    };
    setItems(updatedItems);
  };

  const handleDurationUpdate = (value: MagicPrioritizationItem['duration']) => {
    const updatedItems = [...items];
    updatedItems[currentItemIndex] = {
      ...updatedItems[currentItemIndex],
      duration: value,
    };
    setItems(updatedItems);
  };

  const handleNext = () => {
    // DIMENSION-FIRST APPROACH:
    // 1. Score ALL items on current dimension
    // 2. Then move to next dimension
    // 3. Repeat until all dimensions complete
    
    if (currentItemIndex < items.length - 1) {
      // More items to score in this dimension
      setCurrentItemIndex(currentItemIndex + 1);
    } else {
      // All items scored for this dimension, move to next dimension
      if (scoringPhase === 'urgency') {
        setScoringPhase('value');
        setCurrentItemIndex(0);
      } else if (scoringPhase === 'value') {
        setScoringPhase('duration');
        setCurrentItemIndex(0);
      } else if (scoringPhase === 'duration') {
        // All dimensions complete - calculate final scores
        const calculatedItems = items.map((item) => {
          const weightedScore = calculateWeightedScore(item.urgency, item.value, item.duration);
          const cd3Category = getCd3Category(item.urgency, item.value, item.duration);
          return { ...item, weightedScore, cd3Category };
        });
        setItems(calculatedItems);
        if (!isLiveSession) {
          setState('results');
        }
      }
    }
  };

  const handleBack = () => {
    if (currentItemIndex > 0) {
      // Go back to previous item in same dimension
      setCurrentItemIndex(currentItemIndex - 1);
    } else {
      // At first item - go back to last item of previous dimension
      if (scoringPhase === 'value') {
        setScoringPhase('urgency');
        setCurrentItemIndex(items.length - 1);
      } else if (scoringPhase === 'duration') {
        setScoringPhase('value');
        setCurrentItemIndex(items.length - 1);
      }
      // If at urgency phase and first item, can't go back further
    }
  };

  const handleReset = () => {
    setState('input');
    setItems([]);
    setCurrentItemIndex(0);
    setScoringPhase('urgency');
  };

  const canProceed = () => {
    const current = items[currentItemIndex];
    if (!current) return false;
    
    if (scoringPhase === 'urgency') {
      return current.urgency !== null;
    }
    if (scoringPhase === 'value') {
      return current.value !== null;
    }
    if (scoringPhase === 'duration') {
      return current.duration !== null;
    }
    return false;
  };

  const canGoBack = () => {
    // Can go back unless we're at the very first item of the very first dimension
    return !(scoringPhase === 'urgency' && currentItemIndex === 0);
  };

  const currentItem = items[currentItemIndex];
  
  // Progress calculation:
  // 3 phases, each phase has items.length items
  // Total steps = 3 * items.length
  const phaseIndex = scoringPhase === 'urgency' ? 0 : scoringPhase === 'value' ? 1 : 2;
  const totalSteps = items.length * 3;
  const completedSteps = phaseIndex * items.length + currentItemIndex;
  const progress = state === 'scoring' && totalSteps > 0 
    ? (completedSteps / totalSteps) * 100 
    : 100;

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-6 pb-4 border-b border-[var(--rule)]">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Wand2 className="h-8 w-8 text-[var(--text-ter)] flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-[var(--text-pri)]">
                Magic Prioritization Trick
              </h1>
              <p className="text-sm text-[var(--text-sec)] mt-1">
                Decouple urgency, value, and duration to surface true Cost of Delay. Based on John Cutler's work.
              </p>
            </div>
          </div>
          {state !== 'input' && (
            <button
              onClick={handleReset}
              className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] px-3 py-1 rounded hover:bg-[var(--hover-tint)] whitespace-nowrap"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div>
        {state === 'input' && (
          <ItemInput onStart={handleStart} />
        )}

        {state === 'scoring' && currentItem && isLiveSession && (
          <LiveToolScaffold
            title="Magic prioritization"
            subtitle="Score urgency, then value, then duration — one dimension at a time"
            progressDone={completedSteps}
            progressTotal={totalSteps}
          >
            <MagicPrioritizationLive
              itemName={currentItem.name}
              phase={scoringPhase}
              selectedId={
                scoringPhase === 'urgency'
                  ? currentItem.urgency
                  : scoringPhase === 'value'
                    ? currentItem.value
                    : currentItem.duration
              }
              onSelect={id => {
                if (scoringPhase === 'urgency') handleUrgencyUpdate(id as MagicPrioritizationItem['urgency']);
                else if (scoringPhase === 'value') handleValueUpdate(id as MagicPrioritizationItem['value']);
                else handleDurationUpdate(id as MagicPrioritizationItem['duration']);
              }}
              onNext={handleNext}
              onBack={canGoBack() ? handleBack : undefined}
              canProceed={canProceed()}
              itemIndex={currentItemIndex}
              totalItems={items.length}
              phaseIndex={phaseIndex}
            />
          </LiveToolScaffold>
        )}

        {state === 'scoring' && currentItem && !isLiveSession && (
          <ScoringView
            item={currentItem}
            items={items}
            itemIndex={currentItemIndex}
            totalItems={items.length}
            phase={scoringPhase}
            onUrgencyUpdate={handleUrgencyUpdate}
            onValueUpdate={handleValueUpdate}
            onDurationUpdate={handleDurationUpdate}
            onNext={handleNext}
            onBack={handleBack}
            canProceed={canProceed()}
            canGoBack={canGoBack()}
            progress={progress}
          />
        )}

        {state === 'results' && (
          <ResultsView 
            items={items} 
            onReset={handleReset}
          />
        )}
      </div>
    </div>
  );
}

// Export with ToolWrapper for workspace integration
export default function MagicPrioritization(props: ToolHostProps) {
  const [importHandler, setImportHandler] = useState<((items: ToolItem[]) => void) | null>(null);
  const [resultsHandler, setResultsHandler] = useState<(() => ToolResultItem[]) | null>(null);
  const [itemCount, setItemCount] = useState(0);

  return (
    <ToolWrapper
      toolName="Magic Prioritization Trick"
      toolDescription="Score items on urgency, value, and duration one dimension at a time to prevent conflation"
      onImportItems={importHandler || undefined}
      onGetResults={resultsHandler ?? undefined}
      itemCount={itemCount}
      {...props}
    >
      <MagicPrioritizationContent
        onRegisterImportHandler={(handler) => setImportHandler(() => handler)}
        onRegisterGetResults={(handler) => setResultsHandler(() => handler)}
        onItemCountChange={setItemCount}
      />
    </ToolWrapper>
  );
}
